import { useState, useMemo } from 'react';
import { useApiCall } from '../utils/useApiCall';
import JobsService, { JobData } from '../api/JobsService';
import { JobDetails } from '../types/job';
import { transformJobToDetails } from '../utils/jobTransformations';
import { jobsData } from '../data/jobs';

export const useJobManagement = () => {
  // Search and filter state
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  
  // Job details state
  const [isJobDetailsOpen, setIsJobDetailsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobDetails | null>(null);
  
  // Use the custom hook for API calls to get jobs
  const { 
    data: apiJobs,
    isLoading: isLoadingJobs,
    error: jobsError,
    execute: fetchJobs
  } = useApiCall<JobData[]>(JobsService.getJobs);
  
  // Filter jobs based on search keyword, division, and user
  const filteredJobs = useMemo(() => {
    if (!apiJobs || apiJobs.length === 0) return [];
    
    return apiJobs.filter(job => {
      // First filter by search keyword if provided
      const matchesKeyword = !searchKeyword.trim() || 
                        job.title.toLowerCase().includes(searchKeyword.toLowerCase());
      
      // Then filter by division if a specific division is selected
      const matchesDivision = selectedDivision === 'all' || 
                          job.divisionId.toString() === selectedDivision;
      
      // Then filter by user/recruiter if a specific user is selected
      const matchesUser = selectedUser === 'all' || 
                      job.recruiterId.toString() === selectedUser;
      
      // Job must match all filters
      return matchesKeyword && matchesDivision && matchesUser;
    });
  }, [apiJobs, searchKeyword, selectedDivision, selectedUser]);

  // Handle search functionality
  const handleSearch = (keyword: string, divisionId: string, userId: string) => {
    setSearchKeyword(keyword);
    setSelectedDivision(divisionId);
    setSelectedUser(userId);
  };
  
  // Modified to work with API job data
  const handleJobClick = (jobId: number) => {
    // Find the job in apiJobs
    const job = apiJobs?.find(job => job.id === jobId);
    
    if (job) {
      // Use our utility function to transform the job data
      const jobDetails = transformJobToDetails(job);
      setSelectedJob(jobDetails);
      setIsJobDetailsOpen(true);
    }
  };

  // Handle static job click for demo purposes
  const handleStaticJobClick = (jobKey: 'marketingDirector' | 'devOpsEngineer') => {
    // Create a modified version of the static job data that matches the updated interface
    const staticJobDetails: JobDetails = {
      title: jobsData[jobKey].title,
      location: jobsData[jobKey].location,
      function: jobsData[jobKey].function,
      industry: jobsData[jobKey].industry,
      hostedApplyUrl: jobsData[jobKey].hostedApplyUrl,
      contactEmail: jobsData[jobKey].contactEmail,
      description: `
        <p>${jobsData[jobKey].description}</p>
        <h3>About the Role</h3>
        <p>${jobsData[jobKey].aboutRole}</p>
        <h3>Responsibilities</h3>
        <ul>
          ${jobsData[jobKey].responsibilities.map(item => `<li>${item}</li>`).join('')}
        </ul>
      `,
      requiredSkills: `<ul>${jobsData[jobKey].requiredSkills.map(skill => `<li>${skill}</li>`).join('')}</ul>`
    };
    
    setSelectedJob(staticJobDetails);
    setIsJobDetailsOpen(true);
  };

  return {
    apiJobs,
    isLoadingJobs,
    jobsError,
    fetchJobs,
    filteredJobs,
    searchKeyword,
    selectedDivision,
    selectedUser,
    handleSearch,
    isJobDetailsOpen,
    setIsJobDetailsOpen,
    selectedJob,
    handleJobClick,
    handleStaticJobClick,
    closeJobDetails: () => setIsJobDetailsOpen(false)
  };
}; 